import { useEffect, useCallback } from 'react';

interface PresentationKeysOptions {
  totalSlides: number;
  currentSlide: number;
  onNext: () => void;
  onPrev: () => void;
  onGoTo: (index: number) => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomReset: () => void;
  onExit: () => void;
  onActivity: () => void;
  enabled: boolean;
}

export function usePresentationKeys({
  totalSlides,
  currentSlide,
  onNext,
  onPrev,
  onGoTo,
  onZoomIn,
  onZoomOut,
  onZoomReset,
  onExit,
  onActivity,
  enabled,
}: PresentationKeysOptions) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!enabled) return;

      onActivity();

      switch (e.key) {
        case 'ArrowRight':
        case ' ':
        case 'n':
        case 'N':
          e.preventDefault();
          onNext();
          break;
        case 'ArrowLeft':
        case 'p':
        case 'P':
          e.preventDefault();
          onPrev();
          break;
        case 'Home':
          e.preventDefault();
          onGoTo(0);
          break;
        case 'End':
          e.preventDefault();
          onGoTo(totalSlides - 1);
          break;
        case 'Escape':
          e.preventDefault();
          onExit();
          break;
        case '+':
        case '=':
          e.preventDefault();
          onZoomIn();
          break;
        case '-':
          e.preventDefault();
          onZoomOut();
          break;
        case '0':
          e.preventDefault();
          onZoomReset();
          break;
        default:
          // Number keys 1-9 jump to slide
          if (e.key >= '1' && e.key <= '9') {
            const idx = parseInt(e.key, 10) - 1;
            if (idx < totalSlides) {
              e.preventDefault();
              onGoTo(idx);
            }
          }
          break;
      }
    },
    [enabled, totalSlides, currentSlide, onNext, onPrev, onGoTo, onZoomIn, onZoomOut, onZoomReset, onExit, onActivity],
  );

  useEffect(() => {
    if (!enabled) return;
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled, handleKeyDown]);
}
